import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Button, Container } from 'react-bootstrap';

import { history } from '../../_helpers';
import CommonPageHeader from '../CommonPageHeader/CommonPageHeader';
import { sickNoteService } from '../../services/sicknote.service';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import CircularProgress from '@material-ui/core/CircularProgress';

const PaymentSuccess = props => {
  const { user = {}, location, schoolWorkForm = {} } = props;
  const [paymentStatus, setPaymentStatus] = useState('checking'); // 'checking', 'success', 'error'
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [confirmedFormId, setConfirmedFormId] = useState(null); // Store formId from payment confirmation

  // Determine if this is a school/work form or sick note based on the URL path
  const isSchoolWorkForm = location.pathname.includes('school-work-form');

  useEffect(() => {
    // Check for 3DS return parameters
    const urlParams = new URLSearchParams(location.search);
    const paymentIntentId = urlParams.get('payment_intent');
    const clientSecret = urlParams.get('payment_intent_client_secret');
    const redirectStatus = urlParams.get('redirect_status');

    // console.log('3DS Return check:', { paymentIntentId, clientSecret, redirectStatus });

    if (paymentIntentId && clientSecret) {
      // This is a 3DS return - need to confirm the PaymentIntent
      console.log('🔄 3DS return detected - confirming PaymentIntent:');
      confirmPaymentIntent(paymentIntentId);
    } else {
      // Regular success page (no 3DS) or already processed
      console.log('✅ Regular success page - no 3DS confirmation needed');
      setPaymentStatus('success');
    }
  }, [location.search]);

  const confirmPaymentIntent = async paymentIntentId => {
    try {
      setIsProcessing(true);
      // console.log('🔄 Confirming PaymentIntent on backend:', paymentIntentId);

      // Call backend to confirm PaymentIntent and create tickler
      const response = await sickNoteService.charge({
        payment_intent_id: paymentIntentId
      });

      // console.log('✅ Backend confirmation response:', response.data);

      if (response.data.success) {
        console.log('🎉 Payment confirmed successfully - tickler should be created');

        // Extract accessToken or formId from payment metadata if available (for school-work-form redirects)
        if (response.data.accessToken) {
          console.log('✅ AccessToken extracted from payment confirmation:');
          setConfirmedFormId(response.data.accessToken); // Store accessToken in the same state for redirect
        } else if (response.data.formId) {
          console.log('✅ FormId extracted from payment confirmation:');
          setConfirmedFormId(response.data.formId);
        }

        setPaymentStatus('success');
      } else {
        // console.error('❌ Backend confirmation failed:', response.data);
        setPaymentStatus('error');
        setErrorMessage(response.data.extras?.message || 'Payment confirmation failed');
      }
    } catch (error) {
      console.error('❌ Payment confirmation error:', error);
      setPaymentStatus('error');
      setErrorMessage(error.response?.data?.extras?.message || error.message || 'Payment confirmation failed. Please contact support.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Show loading state while processing 3DS confirmation
  if (isProcessing || paymentStatus === 'checking') {
    return (
      <>
        <CommonPageHeader title="Processing Payment" subtitle={isSchoolWorkForm ? 'school-work-form | payment | processing' : 'sick-note | payment | processing'} />
        <div className="payment-success" style={{ height: '800px' }}>
          <Dialog open={true} fullWidth={true}>
            <DialogTitle>Processing Your Payment</DialogTitle>
            <DialogContent style={{ textAlign: 'center', padding: '40px' }}>
              <CircularProgress size={50} style={{ marginBottom: '20px' }} />
              <div>Please wait while we confirm your payment...</div>
            </DialogContent>
          </Dialog>
        </div>
      </>
    );
  }

  // Show error state
  if (paymentStatus === 'error') {
    return (
      <>
        <CommonPageHeader title="Payment Error" subtitle={isSchoolWorkForm ? 'school-work-form | payment | error' : 'sick-note | payment | error'} />
        <div className="payment-success" style={{ height: '800px' }}>
          <Dialog open={true} fullWidth={true}>
            <DialogTitle>Payment Confirmation Failed</DialogTitle>
            <DialogContent>
              <div>{errorMessage || 'There was an issue confirming your payment. Please contact support with your payment details.'}</div>
              <div style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>Support Email: support@instoremed.com</div>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  if (isSchoolWorkForm && schoolWorkForm.formId) {
                    // For failed school-work-form payments, still try to go to upload page
                    history.push(`/school-work-form/upload/${schoolWorkForm.formId}`);
                  } else {
                    history.push('/dashboard');
                  }
                }}
              >
                {isSchoolWorkForm ? 'Continue to Upload' : 'Back to Dashboard'}
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </>
    );
  }

  // Show success (original component logic)
  const pageTitle = isSchoolWorkForm ? 'Form Request Payment Successful' : 'Sick Note Payment Successful';

  const pageSubtitle = isSchoolWorkForm ? 'school-work-form | payment | success' : 'sick-note | payment | success';

  const dialogTitle = isSchoolWorkForm ? 'Form request submitted successfully' : 'Sick note request submitted successfully';

  const dialogMessage = isSchoolWorkForm
    ? `Your form request has been submitted successfully. Doctor Budai will process your request within 1-2 business days. The email we have on file is ${user.email}.`
    : `Doctor Budai will get back to you within 24 hours through email. The email we have on file is ${user.email}.`;

  return (
    <>
      <CommonPageHeader title={pageTitle} subtitle={pageSubtitle} />

      <div className="payment-success" style={{ height: '800px' }}>
        <Dialog open={true} fullWidth={true}>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogContent>{dialogMessage}</DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                if (isSchoolWorkForm && (confirmedFormId || schoolWorkForm.formId)) {
                  // Redirect to upload page for school-work-form - use token route for accessToken, formId route for formId
                  const identifier = confirmedFormId || schoolWorkForm.formId;

                  // If we have an accessToken (longer string), use token route; otherwise use formId route
                  if (confirmedFormId && confirmedFormId.length > 24) {
                    // AccessToken route (secure post-payment access)
                    // console.log('🔄 Redirecting to upload page with accessToken:', identifier);
                    history.push(`/school-work-form/upload-token/${identifier}`);
                  } else {
                    // FormId route (fallback)
                    // console.log('🔄 Redirecting to upload page with formId:', identifier);
                    history.push(`/school-work-form/upload/${identifier}`);
                  }
                } else {
                  // Default redirect to dashboard
                  console.log('🔄 Redirecting to dashboard');
                  history.push('/dashboard');
                }
              }}
            >
              {isSchoolWorkForm ? 'Continue to Upload Documents' : 'OK'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

const mapStateToProps = state => ({
  user: state.auth.user,
  schoolWorkForm: state.sickNote.schoolWorkForm || state.sickNote.data // Get school work form data for formId
});

export default connect(mapStateToProps)(withRouter(PaymentSuccess));
